import React, { useEffect, useState } from 'react';

import { css } from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colors, HStack, IconButton, Text, VStack } from '@rtkwlf/fenrir-react';
import { faCircleNotch } from '@rtkwlf/pro-solid-svg-icons/faCircleNotch';
import { faExclamationTriangle } from '@rtkwlf/pro-solid-svg-icons/faExclamationTriangle';
import { faTrash } from '@rtkwlf/pro-solid-svg-icons/faTrash';
import { useTranslation } from 'react-i18next';

import { scaleValue } from '@packages/utils/base.utils';

import type { UploadFileFunctionType } from './FileAttachment';

type FileAttachmentListProps = {
  file: File;
  handleFileDelete: (file: string, fileToken: string) => void;
  uploadFile: UploadFileFunctionType;
  maxFileSize?: number;
};

export const MAX_ATTACHMENT_SIZE = 20 * 1000 * 1000;
const ZERO_ATTACHMENT_SIZE = 0;

export const FileAttachmentList: React.FC<FileAttachmentListProps> = ({
  file,
  handleFileDelete,
  uploadFile,
  maxFileSize = MAX_ATTACHMENT_SIZE,
}) => {
  const { t } = useTranslation();
  const [isError, setIsError] = useState<boolean>();
  const [isLoading, setIsLoading] = useState<boolean>();
  const [token, setToken] = useState<string>('');
  const value = scaleValue(file.size, 1024, 0, t);

  const isFileTooBig = file.size > maxFileSize;
  const isFileEmpty = file.size === ZERO_ATTACHMENT_SIZE;

  useEffect(() => {
    if (!uploadFile || isFileTooBig || isFileEmpty) {
      setIsError(true);
      return;
    }

    setIsLoading(true);
    uploadFile({ file })
      .then(({ token: fileToken }) => {
        setIsError(false);
        setToken(fileToken);
      })
      .catch(() => {
        setIsError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [file, uploadFile, isFileTooBig, isFileEmpty]);

  const getErrorMessage = () => {
    if (isFileTooBig)
      return t('fileAttachment:errorMessage.maxSize', {
        maxSize: MAX_ATTACHMENT_SIZE,
      });
    if (isFileEmpty) return t('fileAttachment:errorMessage.zeroSize');
    return t('fileAttachment:errorMessage.fileUpload');
  };

  return (
    <HStack
      key={file.name}
      backgroundColor={isError ? 'dangerHighlight' : 'widgetHighlight'}
      padding="default"
      marginBottom="small"
      borderRadius="default"
    >
      {/* TODO: This loader will replace by Fenrir loader when it will be available. */}
      {isLoading && (
        <FontAwesomeIcon icon={faCircleNotch} className="fa-spin" />
      )}
      {isError && (
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          css={css({
            color: `rgb(${colors.background.danger.default})`,
          })}
        />
      )}
      {isError ? (
        <VStack>
          <Text fontWeight="bold">{file.name}</Text>
          <Text textColor="dangerDefault">{getErrorMessage()}</Text>
        </VStack>
      ) : (
        <>
          <Text fontWeight="bold">{file.name}</Text>
          <Text>{`(${value.num} ${value.suffix})`}</Text>
        </>
      )}

      {!isLoading && (
        <IconButton
          data-testid={`deleteAttachment-${token}`}
          aria-label={t('fileAttachment:deleteFile')}
          title={t('fileAttachment:deleteFile')}
          marginLeft="auto"
          onClick={() => handleFileDelete(file.name, token)}
        >
          <FontAwesomeIcon icon={faTrash} />
        </IconButton>
      )}
    </HStack>
  );
};
